/** Measurement and retry identity shared by quiz and coding submissions. */
export type SubmissionMeasurement = {
  submission_id: string;
  app_session_id: number | null;
  lesson_visit_id: number | null;
  exposure_id?: string;
  exposure_started_at?: string;
  client_submitted_at: string;
  active_answer_ms?: number;
  elapsed_answer_ms?: number;
};
type Kind = "quiz" | "coding";
type Exposure = { id: string; kind: Kind; target: number; started: number; at: string; active: number; tick: number };
let exposure: Exposure | null = null;
let live = { appSessionId: null as number | null, lessonVisitId: null as number | null, active: true };
const pending = new Map<string, { fingerprint: string; measurement: SubmissionMeasurement }>();
const now = () => performance.now();

function tick() {
  if (!exposure) return;
  const time = now();
  if (live.active) exposure.active += Math.max(0, time - exposure.tick);
  exposure.tick = time;
}

export function updateResearchContext(context: typeof live) {
  tick();
  if (live.appSessionId && live.appSessionId !== context.appSessionId) exposure = null;
  live = context;
}

export function beginExposure(kind: Kind, target: number, id: string = crypto.randomUUID()) {
  const time = now();
  exposure = { id, kind, target, started: time, tick: time, at: new Date().toISOString(), active: 0 };
  return exposure.id;
}

export function endExposure(kind: Kind, target: number) {
  if (exposure?.kind === kind && exposure.target === target) exposure = null;
}

export function measureExposure(kind: Kind, target: number) {
  tick();
  if (exposure?.kind !== kind || exposure.target !== target) return null;
  return { exposure_id: exposure.id, active_ms: Math.floor(exposure.active), elapsed_ms: Math.floor(now() - exposure.started) };
}

export function currentExposureId(kind: Kind, target: number) {
  return exposure?.kind === kind && exposure.target === target ? exposure.id : undefined;
}

export function exposureEventData(data?: Record<string, unknown>) {
  if (!exposure) return data;
  const target = data?.question_id ?? data?.exercise_id;
  if (typeof target === "number" && target !== exposure.target) return data;
  return { exposure_id: exposure.id, ...data };
}

// Persist the fingerprint, not student code or answers, across refreshes.
export async function prepareSubmission(student: string, kind: Kind, target: number, payload: object): Promise<SubmissionMeasurement> {
  const key = `faheem-submission:v1:${student}:${kind}:${target}`;
  tick();
  const current = exposure?.kind === kind && exposure.target === target ? exposure : null;
  const measurement: SubmissionMeasurement = {
    submission_id: crypto.randomUUID(), app_session_id: live.appSessionId,
    lesson_visit_id: live.lessonVisitId, client_submitted_at: new Date().toISOString(),
    ...(current ? { exposure_id: current.id, exposure_started_at: current.at,
      active_answer_ms: Math.floor(current.active), elapsed_answer_ms: Math.floor(now() - current.started) } : {}),
  };
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(payload)));
  const fingerprint = Array.from(new Uint8Array(bytes), value => value.toString(16).padStart(2, "0")).join("");
  let saved = pending.get(key);
  if (!saved) {
    try { saved = JSON.parse(window.sessionStorage.getItem(key) ?? "null") ?? undefined; } catch { /* Memory remains usable. */ }
  }
  if (saved?.fingerprint === fingerprint) return saved.measurement;
  saved = { fingerprint, measurement };
  pending.set(key, saved);
  try { window.sessionStorage.setItem(key, JSON.stringify(saved)); } catch { /* Keep retry identity in memory. */ }
  return measurement;
}

export function completeSubmission(student: string, kind: Kind, target: number) {
  const key = `faheem-submission:v1:${student}:${kind}:${target}`;
  pending.delete(key);
  try { window.sessionStorage.removeItem(key); } catch { /* No persisted copy available. */ }
}

export function captureRecordingContext() {
  return {app_session_id:live.appSessionId,lesson_visit_id:live.lessonVisitId};
}
